import { Suspense, lazy } from "react";
import Icon from "@mui/material/Icon";

import SignIn from "layouts/authentication/sign-in";
import SignUp from "layouts/authentication/sign-up";
import PrivacyPolicy from "layouts/PrivacyPolicy";
import ProductForm from "layouts/products/ProductForm";
import ContentOrganizationFiles from "layouts/contentOrganization/components/ContentOrganizationFiles";
import ForgotPassword from "layouts/authentication/forgot-password";
import VerifyOTP from "layouts/authentication/verify-otp";
import ResetPassword from "layouts/authentication/reset-password";
import TermsAndConditions from "layouts/authentication/terms-and-conditions";
import PaymentAgreement from "layouts/authentication/payment-agreement";

const Users = lazy(() => import("layouts/users"));
const Buyers = lazy(() => import("layouts/buyers"));
const Plugins = lazy(() => import("layouts/plugins"));
const Profile = lazy(() => import("layouts/profile"));
const Settings = lazy(() => import("layouts/settings"));
const SalesRep = lazy(() => import("layouts/distributor-dashboard/layout/sales-rep"));
const Products = lazy(() => import("layouts/products"));
const Payments = lazy(() => import("layouts/payments"));
const Dashboard = lazy(() => import("layouts/dashboard"));
const BrandDetail = lazy(() => import("layouts/brandDetail"));
//const PaymentDetails = lazy(() => import("layouts/payments/components/paymentDetails"));
const ContentOrganization = lazy(() => import("layouts/contentOrganization"));
const DistributorDashboard = lazy(() => import("layouts/distributor-dashboard"));
const DistributorSettings = lazy(() => import("layouts/distributor-dashboard/layout/settings"));
const DistributorSalesRepDashboard = lazy(() =>
  import("layouts/distributor-sales-rep-dashboard/index")
);
const SalesRepBrands = lazy(() => import("layouts/distributor-sales-rep-dashboard/layout/brands"));
const SalesRepCustomers = lazy(() =>
  import("layouts/distributor-sales-rep-dashboard/layout/customers")
);
const SalesRepProducts = lazy(() =>
  import("layouts/distributor-sales-rep-dashboard/layout/products")
);

const Distributors = lazy(() => import("layouts/distributor"));
const Invoices = lazy(() => import("layouts/invoices"));
const Brands = lazy(() => import("layouts/brands"));
const Sales = lazy(() => import("layouts/sales"));
const EditBuyer = lazy(() => import("layouts/buyers/editBuyer"));
const AddBuyer = lazy(() => import("layouts/buyers/addBuyer"));
const UploadBuyers = lazy(() => import("layouts/superAdmin/uploadBuyer"));
const SaleDetail = lazy(() => import("layouts/sales/saleDetails"));
const SuperAdminDashboard = lazy(() => import("layouts/superAdmin/dashboard"));
const SuperAdminBrands = lazy(() => import("layouts/superAdmin/brands"));
const SuperAdminDistributors = lazy(() => import("layouts/superAdmin/distributors"));
const SuperAdminProducts = lazy(() => import("layouts/superAdmin/products"));
const SuperAdminSettings = lazy(() => import("layouts/superAdmin/settings"));
const SuperAdminConnections = lazy(() => import("layouts/superAdmin/connections"));
const UploadProducts = lazy(() => import("layouts/superAdmin/uploadProduct"));
const UploadExternalOrders = lazy(() => import("layouts/superAdmin/uploadExternalOrders"));
const UploadSalesRep = lazy(() => import("layouts/superAdmin/uploadSalesRep"));
const AddUser = lazy(() => import("layouts/superAdmin/addUser"));
const UploadSales = lazy(() => import("layouts/sales/uploadSales"));
const BrandDetails = lazy(() => import("layouts/superAdmin/brands/brandDetails"));
const DistributorDetails = lazy(() => import("layouts/superAdmin/distributors/distributorDetails"));
const Billing = lazy(() => import("layouts/billing/index"));
const InvoiceDetails = lazy(() => import("layouts/invoices/invoiceDetails"));
const ProductSaleDetails = lazy(() => import("layouts/sales/product-sales-details"));
const ProductDetails = lazy(() => import("layouts/products/productDetails"));
const UnassociatedProducts = lazy(() => import("layouts/superAdmin/unassociateProducts"));
const BrandPlugins = lazy(() => import("layouts/brand-plugin"));
const BrandInvoices = lazy(() => import("layouts/brand-invoices"));
const AdminInvoices = lazy(() => import("layouts/superAdmin/invoices"));
const ExternalOrders = lazy(() => import("layouts/external-orders"));
const SuperAdminImporters = lazy(() => import("layouts/superAdmin/importers/index"));
const ImporterDashboard = lazy(() => import("layouts/importer-dashboard/index"));
const ImporterDetails = lazy(() => import("layouts/superAdmin/importers/importerDetails/index"));
const Connection = lazy(() => import("layouts/distributor-dashboard/layout/connection"));
const SalesRepDetail = lazy(() => import("layouts/distributor-dashboard/layout/salesRepDetail"));
const DistributorCutomers = lazy(() =>
  import("layouts/distributor-dashboard/layout/customers/index")
);
const CustomerDetailsPage = lazy(() =>
  import("layouts/distributor-dashboard/layout/customerDetails/index")
);

const SuspendedComponent = (Component) => (
  <Suspense>
    <Component />
  </Suspense>
);

export const brandRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: SuspendedComponent(Dashboard),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Products",
    key: "products",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/products",
    component: SuspendedComponent(Products),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Edit Product",
    key: "edit-Product",
    route: "/products/:id",
    component: SuspendedComponent(ProductDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Content Organization",
    key: "content-organization",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/content-organization",
    component: SuspendedComponent(ContentOrganization),
    isPrivate: true,
    isNavbar: true,
    disabled: true,
  },
  {
    name: "Logo",
    key: "logo",
    route: "/content-organization/logo",
    component: SuspendedComponent(ContentOrganizationFiles),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Product Picture",
    key: "product-picture",
    route: "/content-organization/product-picture",
    component: SuspendedComponent(ContentOrganizationFiles),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Other",
    key: "other",
    route: "/content-organization/other",
    component: SuspendedComponent(ContentOrganizationFiles),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Customers",
    key: "customers",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/customers",
    component: SuspendedComponent(Buyers),
    isPrivate: true,
    isNavbar: true,
    disabled: false,
  },
  {
    type: "collapse",
    name: "Distributors",
    key: "distributors",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/distributors",
    component: SuspendedComponent(Distributors),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Plugins",
    key: "plugins",
    icon: <Icon fontSize="small">shopify</Icon>,
    route: "/plugins",
    component: SuspendedComponent(BrandPlugins),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Payments",
    key: "payments",
    icon: <Icon fontSize="small">payments</Icon>,
    route: "/payments",
    component: SuspendedComponent(Billing),
    isPrivate: true,
    isNavbar: true,
  },
  // {
  //   type: "collapse",
  //   name: "Settings",
  //   href: true,
  //   key: "settings",
  //   icon: <Icon fontSize="small">settings</Icon>,
  //   route: "/settings",
  //   component: SuspendedComponent(Settings),
  //   isPrivate: true,
  //   isNavbar: true,
  //   children: [
  //     {
  //       type: "collapse",
  //       name: "Users",
  //       key: "users",
  //       icon: <Icon fontSize="small">person</Icon>,
  //       route: "/users",
  //       component: SuspendedComponent(Users),
  //       isPrivate: true,
  //       isNavbar: true,
  //     },
  //   ],
  // },
  {
    type: "collapse",
    name: "Sales",
    key: "sales",
    icon: <Icon fontSize="small">money</Icon>,
    route: "/sales",
    component: SuspendedComponent(Sales),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Invoices",
    key: "invoices",
    icon: <Icon fontSize="small">receipt</Icon>,
    route: "/invoices",
    component: SuspendedComponent(BrandInvoices),
    isPrivate: true,
    isNavbar: true,
    disabled: false,
  },
  {
    name: "Product Sales Detail",
    key: "product-sale-detail",
    icon: <Icon fontSize="small">money</Icon>,
    route: "/sales/:productId",
    component: SuspendedComponent(ProductSaleDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Sales Detail",
    key: "sale-detail",
    icon: <Icon fontSize="small">money</Icon>,
    route: "/sales/:id",
    component: SuspendedComponent(SaleDetail),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: SuspendedComponent(Settings),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Forgot Password",
    key: "forgot-password",
    route: "/forgot-password",
    component: <ForgotPassword />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Verify Otp",
    key: "verify-otp",
    route: "/verify-otp",
    component: <VerifyOTP />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Reset Password",
    key: "reset-password",
    route: "/reset-password",
    component: <ResetPassword />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Privacy Policy",
    key: "privacyPolicy",
    route: "/privacy-policy",
    component: <PrivacyPolicy />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Terms and Conditions",
    key: "terms-and-conditions",
    route: "/terms-and-conditions",
    component: <TermsAndConditions />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Payment Agreement",
    key: "payment-agreement",
    route: "/payment-agreement",
    component: <PaymentAgreement />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Edit Product",
    key: "edit-product",
    route: "/product/:id",
    component: SuspendedComponent(ProductForm),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Edit Buyer",
    key: "edit-buyer",
    route: "/customers/:id",
    component: SuspendedComponent(EditBuyer),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Add Buyer",
    key: "add-Buyer",
    route: "/buyer/add",
    component: SuspendedComponent(AddBuyer),
    isPrivate: "true",
    isNavbar: "true",
  },
  {
    name: "Add Product",
    key: "add-Product",
    route: "/product/add",
    component: SuspendedComponent(ProductForm),
    isPrivate: "true",
    isNavbar: "true",
  },
  {
    name: "Profile",
    key: "Profile",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/profile",
    component: SuspendedComponent(Profile),
    isPrivate: true,
    isNavbar: true,
  },
];

export const distributorRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: SuspendedComponent(DistributorDashboard),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Connection",
    key: "connection",
    icon: <Icon fontSize="small">link</Icon>,
    route: "/connection",
    component: SuspendedComponent(Connection),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Brands Details",
    key: "Brands-Details",
    route: "/brand-detail/:id",
    component: SuspendedComponent(BrandDetail),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Customers",
    key: "customers",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/customers",
    component: SuspendedComponent(DistributorCutomers),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Edit Buyer",
    key: "edit-buyer",
    route: "/customers/:id",
    component: SuspendedComponent(CustomerDetailsPage),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sales",
    key: "sales",
    icon: <Icon fontSize="small">attach_money</Icon>,
    route: "/sales",
    component: SuspendedComponent(Sales),
    isPrivate: true,
    isNavbar: true,
    disabled: true,
  },
  {
    type: "collapse",
    name: "Sales Rep",
    key: "sales-rep",
    icon: <Icon fontSize="small">attach_money</Icon>,
    route: "/sales-rep",
    component: SuspendedComponent(SalesRep),
    isPrivate: true,
    isNavbar: true,
    disabled: process.env.REACT_APP_ENV === "PROD",
  },
  {
    name: "Sales Rep Detail",
    key: "sales-rep-detail",
    icon: <Icon fontSize="small">attach_money</Icon>,
    route: "/sales-rep/:id",
    component: SuspendedComponent(SalesRepDetail),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Plugins",
    key: "plugins",
    icon: <Icon fontSize="small">shopify</Icon>,
    route: "/plugins",
    component: SuspendedComponent(Plugins),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "External Orders",
    key: "external-orders",
    icon: <Icon fontSize="small">receipt</Icon>,
    route: "/external-orders",
    component: SuspendedComponent(ExternalOrders),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: SuspendedComponent(DistributorSettings),
    isPrivate: true,
    isNavbar: true,
  },
];

export const distributorSalesRepRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: SuspendedComponent(DistributorSalesRepDashboard),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Brands",
    key: "brands",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/brands",
    component: SuspendedComponent(SalesRepBrands),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Products",
    key: "products",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/products",
    component: SuspendedComponent(SalesRepProducts),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Customers",
    key: "customers",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/customers",
    component: SuspendedComponent(SalesRepCustomers),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    isPrivate: false,
    isNavbar: false,
  },
];

export const importerRoutes = [
  {
    type: "collapse",
    name: "Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/dashboard",
    component: SuspendedComponent(ImporterDashboard),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    isPrivate: false,
    isNavbar: false,
  },
];

export const superAdminRoutes = [
  {
    type: "collapse",
    name: "Admin Dashboard",
    key: "dashboard",
    icon: <Icon fontSize="small">table</Icon>,
    route: "/dashboard",
    component: SuspendedComponent(SuperAdminDashboard),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Distributors",
    key: "distributors",
    icon: <Icon fontSize="small">people</Icon>,
    route: "/distributors",
    component: SuspendedComponent(SuperAdminDistributors),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Distributor Details",
    key: "admin-distributor-details",
    route: "/distributors/:id",
    component: SuspendedComponent(DistributorDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Brands",
    key: "brands",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/brands",
    component: SuspendedComponent(SuperAdminBrands),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Importers",
    key: "importers",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/importers",
    component: SuspendedComponent(SuperAdminImporters),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Importer Details",
    key: "admin-importer-details",
    route: "/importers/:id",
    component: SuspendedComponent(ImporterDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Brands Details",
    key: "admin-brands-details",
    route: "/brands/:id",
    component: SuspendedComponent(BrandDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Customers",
    key: "customers",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/customers",
    component: SuspendedComponent(Buyers),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Products",
    key: "products",
    icon: <Icon fontSize="small">storefront</Icon>,
    route: "/products",
    component: SuspendedComponent(SuperAdminProducts),
    isPrivate: true,
    isNavbar: true,
  },
  {
    name: "Edit Product",
    key: "edit-Product",
    route: "/products/:id",
    component: SuspendedComponent(ProductDetails),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Settings",
    key: "settings",
    icon: <Icon fontSize="small">settings</Icon>,
    route: "/settings",
    component: SuspendedComponent(SuperAdminSettings),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Connections",
    key: "connections",
    icon: <Icon fontSize="small">link</Icon>,
    route: "/connections",
    component: SuspendedComponent(SuperAdminConnections),
    isPrivate: true,
    isNavbar: true,
    disabled: true,
  },
  {
    type: "collapse",
    name: "Unassociated Products",
    key: "unassociated-products",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/unassociated-products",
    component: SuspendedComponent(UnassociatedProducts),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Invoices",
    key: "invoices",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/invoices",
    component: SuspendedComponent(AdminInvoices),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Upload Customers",
    key: "upload-buyers",
    icon: <Icon fontSize="small">upload</Icon>,
    route: "/upload-buyers",
    component: SuspendedComponent(UploadBuyers),
    isPrivate: true,
    isNavbar: true,
    disabled: true,
  },
  {
    type: "collapse",
    name: "Upload Products",
    key: "upload-product",
    icon: <Icon fontSize="small">upload</Icon>,
    route: "/upload-product",
    component: SuspendedComponent(UploadProducts),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Upload Sales",
    key: "upload-sale",
    icon: <Icon fontSize="small">upload</Icon>,
    route: "/upload-sale",
    component: SuspendedComponent(UploadSales),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Upload Sales Rep",
    key: "upload-sales-rep",
    icon: <Icon fontSize="small">upload</Icon>,
    route: "/upload-sales-rep",
    component: SuspendedComponent(UploadSalesRep),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Upload External Orders",
    key: "upload-external-orders",
    icon: <Icon fontSize="small">upload</Icon>,
    route: "/upload-external-orders",
    component: SuspendedComponent(UploadExternalOrders),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Admin Users",
    key: "add-user",
    icon: <Icon fontSize="small">person</Icon>,
    route: "/add-user",
    component: SuspendedComponent(AddUser),
    isPrivate: true,
    isNavbar: true,
  },
  {
    type: "collapse",
    name: "Sign In",
    key: "sign-in",
    icon: <Icon fontSize="small">login</Icon>,
    route: "/authentication/sign-in",
    component: <SignIn />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    type: "collapse",
    name: "Sign Up",
    key: "sign-up",
    icon: <Icon fontSize="small">assignment</Icon>,
    route: "/authentication/sign-up",
    component: <SignUp />,
    isPrivate: false,
    isNavbar: false,
  },
  {
    name: "Edit Buyer",
    key: "edit-buyer",
    route: "/customers/:id",
    component: SuspendedComponent(EditBuyer),
    isPrivate: true,
    isNavbar: true,
  },
];
